import { Box, Button, Stack, Typography } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { DataTable } from '../../components/ui/DataTable'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { themeColors } from '../../app/layout/theme'
import { Task as ApiTask } from '../../lib/api/tasksApi'
import { Task } from '../../types'
import { formatDate } from '../../util/helpers'

interface TaskListProps {
  tasks: (Task | ApiTask)[]
  isLoading: boolean
  onNewClick: () => void
  onTaskClick?: (task: Task | ApiTask) => void
}

const isApiTask = (task: Task | ApiTask): task is ApiTask => {
  return 'title' in task && !('subject' in task)
}

const mapApiTaskToTask = (apiTask: ApiTask): Task => ({
  id: apiTask.id,
  subject: apiTask.title,
  status: apiTask.status === 'DONE' ? 'Completed' : 'Not Started',
  priority: 'Medium',
  dueDate: apiTask.dueDate ?? undefined,
  assignedTo: apiTask.assignedTo?.name ?? 'Unassigned',
  createdAt: apiTask.createdAt,
  updatedAt: apiTask.updatedAt,
})

export const TaskList = ({ tasks, isLoading, onNewClick, onTaskClick }: TaskListProps) => {
  const normalizedTasks = tasks.map(task => isApiTask(task) ? mapApiTaskToTask(task) : task) as Task[]
  
  const columns = [
    { id: 'subject', label: 'Subject' },
    {
      id: 'status',
      label: 'Status',
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      render: (val: string | undefined) => (val ? formatDate(val) : 'N/A'),
    },
    { id: 'priority', label: 'Priority' },
    { id: 'assignedTo', label: 'Assigned To' },
  ]

  if (isLoading) {
    return <LoadingSpinner fullHeight />
  }

  if (tasks.length === 0) {
    return (
      <Box
        sx={{
          padding: 4,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography sx={{ color: themeColors.text.secondary, fontSize: '16px' }}>
          Ahh, a clean slate
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewClick}
          sx={{ backgroundColor: themeColors.primary }}
        >
          New Task
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewClick}
            sx={{ backgroundColor: themeColors.primary }}
          >
            New Task
          </Button>
        </Box>
        <DataTable
          columns={columns}
          rows={normalizedTasks}
          onRowClick={(task) => onTaskClick?.(task as Task | ApiTask)}
          emptyMessage="No tasks found"
        />
      </Stack>
    </Box>
  )
}
